import {
 useState
} from 'react'
import type {
 AuthProvider
} from '../client'
import {
 OnboardingEvents, trackEvent
} from '../analytics/posthog'
import {
 authService
} from '../services/api'
import {
 isValidEmail
} from '../utils/validation'

interface SignInHelpFormProps {
  provider: AuthProvider
  // The login page's `?reason=`, a closed token. Never `?error=`, which on some
  // exits is a sentence holding the address the person signed in with.
  reason: string | null
  initialEmail: string
}

type SendState = 'idle' | 'sending' | 'sent' | 'failed'

/**
 * Offered under a failed sign-in's error banner (GET-20068). Many of these are
 * work accounts whose IT setup blocks the sign-in, which support can sort out
 * but never heard about: the person was left with a red banner and nothing to
 * do. The address goes to the support inbox, never to analytics. The login page
 * counts the showing, once per visit.
 */
export function SignInHelpForm({ provider, reason, initialEmail }: SignInHelpFormProps): JSX.Element {
  const [email, setEmail] = useState(initialEmail)
  const [sendState, setSendState] = useState<SendState>('idle')
  const [emailInvalid, setEmailInvalid] = useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (!isValidEmail(email)) {
      setEmailInvalid(true)
      return
    }
    // Off-screen trap field; a bot that fills every input fills this one too.
    const trap = new FormData(event.currentTarget).get('company_website')
    const companyWebsite = typeof trap === 'string' && trap !== '' ? trap : null
    setSendState('sending')
    const sent = await authService.requestSignInHelp(email.trim(), provider, reason, companyWebsite)
      .then(() => true, () => false)
    setSendState(sent ? 'sent' : 'failed')
    trackEvent(OnboardingEvents.SIGN_IN_HELP_SUBMITTED, { provider, reason, sent })
  }

  if (sendState === 'sent') {
    return (
      <div role="status" className="mb-6 p-4 bg-catch-beige rounded-lg">
        <p className="text-sm text-catch-black">
          Thanks. We&apos;ll email {email.trim()} to help you sign in.
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={(event) => void handleSubmit(event)}
      className="mb-6 p-4 bg-catch-beige rounded-lg space-y-3"
      aria-labelledby="sign-in-help-heading"
    >
      <div>
        <p id="sign-in-help-heading" className="text-sm font-medium text-catch-black">
          We can help you get in
        </p>
        <p className="text-sm text-catch-black/60 mt-1">
          Work accounts sometimes need a setting changed before they can sign in. Leave your email and we&apos;ll help you through it.
        </p>
      </div>
      <div>
        <label htmlFor="sign-in-help-email" className="sr-only">Your work email</label>
        <input
          id="sign-in-help-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => { setEmail(event.target.value); setEmailInvalid(false) }}
          placeholder="Your work email"
          className="w-full px-4 py-3 bg-catch-white border border-catch-beige-dark rounded-xl focus:outline-none focus:ring-2 focus:ring-catch-red/30 focus:border-catch-red/50 text-catch-black placeholder-catch-black/40 transition-[box-shadow,border-color]"
          disabled={sendState === 'sending'}
        />
        {emailInvalid && <p className="mt-1 text-sm text-catch-red">Enter the email you sign in with.</p>}
      </div>
      <input
        type="text"
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        defaultValue=""
        className="absolute -left-[9999px] h-px w-px opacity-0"
      />
      <button
        type="submit"
        disabled={sendState === 'sending'}
        className="w-full bg-catch-black text-catch-white px-6 py-3 rounded-xl font-medium hover:bg-catch-black/90 focus:outline-none focus:ring-2 focus:ring-catch-red/30 focus:ring-offset-2 transition-[background-color,box-shadow,transform] duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.96]"
      >
        {sendState === 'sending' ? 'Sending…' : 'Get help signing in'}
      </button>
      {sendState === 'failed' && (
        <p role="alert" className="text-sm text-catch-red">
          That didn&apos;t go through. Email support@catchagent.ai and we&apos;ll help from there.
        </p>
      )}
    </form>
  )
}
